import { ClaimType } from '@monolog/shared/types/claim.types';
import GatewayBase from './gateway-base';

export default class ClaimsRestService extends GatewayBase {
  private claimsForOrder = (orderId: string) => {
    return this.client.get<any[]>(`/v1/claims/order/${orderId}`);
  };

  private getPoliciesByOrderId = (orderId: string) => {
    return this.client.get<any>(`/v1/policies/${orderId}`);
  };

  private createPolicy = (data: any) => {
    return this.client.post<any>('/v1/policies', data);
  };

  get policies() {
    return {
      create: this.createPolicy,
      forOrder: this.getPoliciesByOrderId,
    };
  }
  get claims() {
    return {
      counts: {
        forStores: this.getClaimCountsForStore,
        resolved: this.getResolvedCount,
      },
      credits: {
        delete: this.deleteStoreCredit,
        export: this.exportCredits,
        list: {
          get: this.getStoreCredits,
        },
        usage: {
          get: this.getClaimCreditUsage,
        },
      },
      export: {
        forStore: this.exportClaims,
      },
      forOrder: this.claimsForOrder,
      forStores: this.claimsForStore,
      gallery: {
        counts: {
          forStores: this.claimGalleryCountsForStore,
        },
        forStores: this.claimGalleryForStores,
      },
      search: this.searchClaims,
      warranties: {
        counts: {
          forStores: this.getWarrantyCountsForStores,
        },
        export: this.exportWarranties,
        forStores: this.getWarranties,
        validate: this.validateWarranty,
      },
    };
  }

  private exportClaims = (body: unknown) => {
    return this.client.post<any>(`/v1/claims/export-claims`, body);
  };

  get reviews() {
    return {
      count: this.getReviewsCount,
      export: this.exportReviews,
      get: this.getReviews,
    };
  }
  private exportCredits = (body: unknown) => {
    return this.client.post<any>(`/v1/claims/credits/export`, body);
  };

  private deleteStoreCredit = (storeCreditId: string, storeId) => {
    return this.client.delete(`/v1/claims/credits/${storeCreditId}`, {
      params: { storeId },
    });
  };

  private getStoreCredits = (
    storeId: string,
    params?: {
      filters?: string;
      page?: number;
      limit?: number;
      createdAtMin?: Date;
      createdAtMax?: Date;
    },
  ) => {
    return this.client.get<any>(`/v1/claims/credits/list/${storeId}`, { params });
  };

  private getClaimCountsForStore = (params?: { filters?: string; createdAtMin?: Date; createdAtMax?: Date }) => {
    return this.client.get<any>('/v1/claims/store/counts', {
      params: {
        ...params,
      },
    });
  };

  private searchClaims = (params?: { filters?: string }) => {
    return this.client.get<any>(`/v1/claims/search/${params?.filters}`);
  };

  private claimsForStore = (params?: {
    filters?: string;
    page?: number;
    limit?: number;
    createdAtMin?: Date;
    createdAtMax?: Date;
  }) => {
    return this.client.get<any>(`/v1/claims/list/store`, {
      params: {
        ...params,
      },
    });
  };

  private claimGalleryCountsForStore = (storeIds: string[], claimTypes: ClaimType[], params?: { filters?: string }) => {
    const claimTypesString = claimTypes.join(',');

    return this.client.get<any>(`/v1/claims/gallery/counts`, {
      params: { claimTypes: claimTypesString, ...params },
    });
  };

  private claimGalleryForStores = (
    storeIds: string[],
    claimTypes: ClaimType[],
    params?: { filters?: string; page?: number; limit?: number; createdAtMin?: Date; createdAtMax?: Date },
  ) => {
    const claimTypesString = claimTypes.join(',');

    return this.client.get<any>(`/v1/claims/gallery/list`, {
      params: { claimTypes: claimTypesString, ...params },
    });
  };

  private getClaimCreditUsage = (orderId: string) => {
    return this.client.get(`/v1/claims/credits/usages/${orderId}`);
  };

  private getResolvedCount = (storeId: string) => {
    return this.client.get(`/v1/claims/${storeId}/claim-resolutions/resolved-count`);
  };

  private getReviews = (
    storeId: string,
    params?: { page?: number; limit?: number; createdAtMin?: Date; createdAtMax?: Date },
  ) => {
    return this.client.get(`/v1/claims/reviews/store/${storeId}`, { params });
  };

  private getReviewsCount = (storeId: string, params?: { createdAtMin?: Date; createdAtMax?: Date }) => {
    return this.client.get(`/v1/claims/reviews/store/${storeId}/count`, { params });
  };

  private exportReviews = (storeId: string, body?: { createdAtMin?: Date; createdAtMax?: Date }) => {
    return this.client.post(`/v1/claims/reviews/store/${storeId}/export`, body);
  };

  private getWarranties = (storeIds: string[], params?: { filters?: string }) => {
    return this.client.get(`/v1/claims/warranties/list`, {
      params,
    });
  };

  private getWarrantyCountsForStores = (storeIds: string[], params?: { filters?: string }) => {
    return this.client.get(`/v1/claims/warranties/counts`, {
      params,
    });
  };

  private exportWarranties = (body: { timezone: string; storeIds: string[]; filters: string }) => {
    return this.client.post<any>(`/v1/claims/warranties/export`, body);
  };

  private validateWarranty = (
    claimId: string,
    body: { dateOfPurchase: Date; productValue: number; isValidated: boolean; product?: any },
  ) => {
    return this.client.post(`/v1/claims/warranties/validate/${claimId}`, body);
  };
}
